/*
 * APP ROUTING AND SCREEN PICKER
 * =============================
 * This file decides which page the user sees.
 *
 * It also handles the app's two special cases:
 * - normal internal pages for staff users
 * - the separate referral portal under `/refer`
 *
 * If you later add a new page, this is usually the place where it becomes visible.
 */

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/Auth/AuthContext';
import Login from '@/components/Auth/Login';
import FeedbackReporter from '@/components/Feedback/FeedbackReporter';
import BottomNavigation from '@/components/Layout/BottomNavigation';
import RequireAuth from '@/routes/RequireAuth';
import RequireCompanyAiDeployment from '@/routes/RequireCompanyAiDeployment';
import RequireAiEmailReviewAccess from '@/routes/RequireAiEmailReviewAccess';
import RequireRole from '@/routes/RequireRole';

// Import all page components
import Home from '@/pages/Home';
import ClientList from '@/pages/Clients/ClientList';
import ProjectList from '@/pages/Projects/ProjectList';
import ProjectControlCentrePage from '@/pages/ProjectControlCentrePage';
import ProjectDetail from '@/pages/Projects/ProjectDetail';
import ProjectClientNotificationsPage from '@/pages/Projects/ProjectClientNotificationsPage';
import InvoiceList from '@/pages/Invoices/InvoiceList';
import SmsReviewPage from '@/pages/Notifications/SmsReviewPage';
import InvoiceDetail from '@/pages/Invoices/InvoiceDetail';
import QuotationList from '@/pages/Quotations/QuotationList';
import QuotationDetail from '@/pages/Quotations/QuotationDetail';
import CertificateList from '@/pages/Certificates/CertificateList';
import TaskList from '@/pages/Tasks/TaskList';
import UserList from '@/pages/Users/UserList';
import TemplateList from '@/pages/Templates/TemplateList';
import PDFTest from '@/pages/PDFTest';
import TestAzureSQL from '@/pages/TestAzureSQL';
import Styleguide from '@/pages/Styleguide';
import StyleGalleryPage from '@/pages/style-gallery';
import QuotationWorkflowPage from '@/pages/Workflow/QuotationWorkflowPage';
import InvoiceWorkflowPage from '@/pages/Workflow/InvoiceWorkflowPage';
import WorkflowDraftsPage from '@/pages/Workflow/WorkflowDraftsPage';
import OutlookInvoiceHandoffPage from '@/pages/Workflow/OutlookInvoiceHandoffPage';
import OutlookQuotationHandoffPage from '@/pages/Workflow/OutlookQuotationHandoffPage';
import CertificateWorkflowPage from '@/pages/Workflow/CertificateWorkflowPage';
import AiWorkspacePage from '@/pages/AI/AiWorkspacePage';
import AiAdminPage from '@/pages/AI/AiAdminPage';
import AiEmailPage from '@/pages/AI/AiEmailPage';
import ReferralPortalLandingPage from '@/pages/ReferralPortal/ReferralPortalLandingPage';
import ReferralPortalLoginPage from '@/pages/ReferralPortal/ReferralPortalLoginPage';
import ReferralPortalJoinPage from '@/pages/ReferralPortal/ReferralPortalJoinPage';
import ReferralPortalRequestQuotePage from '@/pages/ReferralPortal/ReferralPortalRequestQuotePage';
import ReferralPortalDashboardPage from '@/pages/ReferralPortal/ReferralPortalDashboardPage';
import FeedbackExportRelayPage from '@/pages/Feedback/FeedbackExportRelayPage';
import QuotationSigningPage from '@/pages/QuotationSigning/QuotationSigningPage';
import IfcViewerPage from '@/pages/Viewer/IfcViewerPage';
import ProjectFilesPortalPage from '@/pages/ProjectFiles/ProjectFilesPortalPage';
import RequirePortalSession from '@/routes/RequirePortalSession';

// The referral portal runs on a special domain so it can behave like a separate mini-site.
const REFERRAL_PORTAL_HOSTS = new Set(['refer.linfinityeng.com']);

function isReferralPortalHost() {
  if (typeof window === 'undefined') {
    return false;
  }
  return REFERRAL_PORTAL_HOSTS.has(window.location.hostname.toLowerCase());
}

function InternalAppRoute({ children }: { children: JSX.Element }) {
  if (isReferralPortalHost()) {
    return <Navigate to="/refer" replace />;
  }
  return children;
}

// If someone pastes a URL with capital letters, send them to the same page in lowercase.
function LowercaseRedirect() {
  const location = useLocation();
  const lower = (location.pathname || "").toLowerCase();

  console.log('🔄 LowercaseRedirect check:', {
    original: location.pathname,
    lowercase: lower,
    hasState: !!location.state,
    state: location.state
  });

  if (location.pathname !== lower) {
    console.log('✅ LowercaseRedirect: Redirecting to lowercase with preserved state');
    // preserve query + hash + state
    return <Navigate
      to={lower + location.search + location.hash}
      state={location.state}
      replace
    />;
  }

  console.log('❌ LowercaseRedirect: Unknown route, redirecting to home');
  // if it's truly an unknown route, send home (or 404 component)
  return <Navigate to={isReferralPortalHost() ? "/refer" : "/"} replace />;
}

function LocalAuthRouteBridge() {
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const postLoginRedirect = search.get('post_login_redirect_uri');
  const postLogoutRedirect = search.get('post_logout_redirect_uri');
  const fallback = postLoginRedirect || postLogoutRedirect || '/';

  console.log('🧪 Local auth route bridge redirecting to:', fallback);
  return <Navigate to={fallback} replace />;
}

// This page is only shown when the user is not signed in.
// If they already have a valid session, send them straight through to their last page.
function LoginRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      let to = "/";
      try {
        const stored = sessionStorage.getItem("postLoginRedirect");
        if (stored) {
          to = stored;
          sessionStorage.removeItem("postLoginRedirect");
        } else {
          const from = (location.state as any)?.from?.pathname;
          if (from) to = from;
        }
      } catch {}
      console.log('🔄 LoginRoute: User authenticated, redirecting to:', to);
      navigate(to, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, location.state]);

  if (isLoading) return null;
  return isAuthenticated ? null : <Login />;
}

/*
 * MOBILE LAYOUT WRAPPER
 * =====================
 * This wrapper keeps the app comfortable on phones by:
 * - using one shared background
 * - reserving room for the bottom nav
 * - filling the screen height cleanly on mobile browsers
 */
const MobileLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const path = (pathname || "").toLowerCase();
  const isReferralPortal = path.startsWith('/refer');

  // These are the main staff pages that should keep the bottom navigation visible on phones.
  const showBottomNav = [
    '/', '/dashboard', '/projects', '/project-control-centre', '/invoices', '/quotations',
    '/clients', '/certificates', '/tasks', '/users', '/templates'
  ].includes(path);

  return (
    <div style={{
      minHeight: 'max(100vh, 100dvh)',              // Use the tallest browser height the device reports.
      backgroundColor: isReferralPortal ? 'transparent' : '#f5f5f5',      // Keep the main app on a soft gray background.
      paddingBottom: showBottomNav ? 'calc(80px + env(safe-area-inset-bottom))' : '0' // Leave room for the bottom bar and phone safe area.
    }}>
      {children}
      <FeedbackReporter />
      {/* Show bottom navigation only on the main staff pages. */}
      {showBottomNav && <BottomNavigation />}
    </div>
  );
};

/*
 * MAIN ROUTER COMPONENT
 * ====================
 * This handles authentication and routing logic using SWA auth.
 */
const AppRouter = () => {
  const { isLoading, authReady } = useAuth();
  const publicEntryPath = typeof window !== 'undefined' && (
    window.location.pathname.toLowerCase().startsWith('/refer') ||
    window.location.pathname.toLowerCase().startsWith('/quotation-signing') ||
    window.location.pathname.toLowerCase().startsWith('/viewer/ifc') ||
    window.location.pathname.toLowerCase().startsWith('/projects/access') ||
    window.location.pathname.toLowerCase().startsWith('/projects/ifc') ||
    window.location.pathname.toLowerCase().startsWith('/projects/files') ||
    isReferralPortalHost()
  );

  // Show a simple loading screen while we confirm whether the user has a session.
  if ((!authReady || isLoading) && !publicEntryPath) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#1a1a1a',    // Dark background - CUSTOMIZE COLOR HERE
        color: '#fff'                   // White text - CUSTOMIZE COLOR HERE
      }}>
        <div style={{ fontSize: '32px', marginBottom: '16px' }}>⏳</div>
        <div>Loading...</div>
      </div>
    );
  }

  // Once auth is ready, render the full route map.
  return (
    <Router>
      <MobileLayout>
        <Routes>
          {/* PUBLIC ROUTES */}
          <Route path="/login" element={isReferralPortalHost() ? <Navigate to="/refer/login" replace /> : <LoginRoute />} />
          <Route path="/.auth/*" element={<LocalAuthRouteBridge />} />
          <Route path="/feedback-export-relay" element={<FeedbackExportRelayPage />} />
          <Route path="/quotation-signing" element={<QuotationSigningPage />} />
          <Route path="/projects/access" element={<ProjectFilesPortalPage />} />
          <Route path="/projects/access/:token" element={<ProjectFilesPortalPage />} />
          <Route path="/projects/ifc" element={<IfcViewerPage />} />
          <Route path="/projects/ifc/:token" element={<IfcViewerPage />} />
          <Route path="/projects/files" element={<ProjectFilesPortalPage />} />
          <Route path="/projects/files/:token" element={<ProjectFilesPortalPage />} />
          <Route path="/viewer/ifc" element={<IfcViewerPage />} />
          <Route path="/viewer/ifc/:token" element={<IfcViewerPage />} />
          <Route path="/refer" element={<ReferralPortalLandingPage />} />
          <Route path="/refer/login" element={<ReferralPortalLoginPage />} />
          <Route path="/refer/join" element={<ReferralPortalJoinPage />} />
          <Route path="/refer/request-quote" element={<RequirePortalSession><ReferralPortalRequestQuotePage /></RequirePortalSession>} />
          <Route path="/refer/dashboard" element={<RequirePortalSession><ReferralPortalDashboardPage /></RequirePortalSession>} />

          {/* PROTECTED ROUTES */}
          <Route path="/" element={isReferralPortalHost() ? <Navigate to="/refer" replace /> : <InternalAppRoute><RequireAuth><Home /></RequireAuth></InternalAppRoute>} />
          <Route path="/dashboard" element={<InternalAppRoute><RequireAuth><Home /></RequireAuth></InternalAppRoute>} />
          <Route path="/clients" element={<InternalAppRoute><RequireAuth><ClientList /></RequireAuth></InternalAppRoute>} />
          <Route path="/projects" element={<InternalAppRoute><RequireAuth><ProjectList /></RequireAuth></InternalAppRoute>} />
          <Route path="/project-control-centre" element={<InternalAppRoute><RequireAuth><RequireRole requiredRoles={['pm', 'admin']} message="Portfolio planning is available to project managers and administrators with company-wide project access."><ProjectControlCentrePage /></RequireRole></RequireAuth></InternalAppRoute>} />
          <Route path="/projects/:projectId/client-updates" element={<InternalAppRoute><RequireAuth><RequireRole requiredRoles={['pm', 'admin']} title="Client update access is limited" message="Project progress notifications are available to project managers and administrators only."><ProjectClientNotificationsPage /></RequireRole></RequireAuth></InternalAppRoute>} />
          <Route path="/projects/:projectId" element={<InternalAppRoute><RequireAuth><ProjectDetail /></RequireAuth></InternalAppRoute>} />
          <Route path="/invoices" element={<InternalAppRoute><RequireAuth><InvoiceList /></RequireAuth></InternalAppRoute>} />
          <Route path="/notifications/sms" element={<InternalAppRoute><RequireAuth><RequireRole requiredRoles={['finance', 'admin']}><SmsReviewPage /></RequireRole></RequireAuth></InternalAppRoute>} />
          <Route path="/invoices/workflow" element={<InternalAppRoute><RequireAuth><InvoiceWorkflowPage /></RequireAuth></InternalAppRoute>} />
          <Route path="/workflow-drafts" element={<InternalAppRoute><RequireAuth><WorkflowDraftsPage /></RequireAuth></InternalAppRoute>} />
          <Route path="/outlook/invoice-handoff" element={<InternalAppRoute><RequireAuth><OutlookInvoiceHandoffPage /></RequireAuth></InternalAppRoute>} />
          <Route path="/outlook/quotation-handoff" element={<InternalAppRoute><RequireAuth><RequireRole requiredRoles={['finance']} title="Quotation workflow access is limited" message="The quotation workflow is currently available to finance or admin users only."><OutlookQuotationHandoffPage /></RequireRole></RequireAuth></InternalAppRoute>} />
          <Route path="/invoices/:invoiceId" element={<InternalAppRoute><RequireAuth><InvoiceDetail /></RequireAuth></InternalAppRoute>} />
          <Route path="/quotations" element={<InternalAppRoute><RequireAuth><RequireRole requiredRoles={['finance']} title="Quotation access is limited" message="The quotation area is currently available to finance or admin users only."><QuotationList /></RequireRole></RequireAuth></InternalAppRoute>} />
          <Route path="/quotations/workflow" element={<InternalAppRoute><RequireAuth><RequireRole requiredRoles={['finance']} title="Quotation workflow access is limited" message="The quotation workflow is currently available to finance or admin users only."><QuotationWorkflowPage /></RequireRole></RequireAuth></InternalAppRoute>} />
          <Route path="/quotations/:quotationId" element={<InternalAppRoute><RequireAuth><RequireRole requiredRoles={['finance']} title="Quotation detail access is limited" message="Quotation details are currently available to finance or admin users only."><QuotationDetail /></RequireRole></RequireAuth></InternalAppRoute>} />
          <Route path="/certificates" element={<InternalAppRoute><RequireAuth><CertificateList /></RequireAuth></InternalAppRoute>} />
          <Route path="/certificates/workflow" element={<InternalAppRoute><RequireAuth><CertificateWorkflowPage /></RequireAuth></InternalAppRoute>} />
          <Route path="/tasks" element={<InternalAppRoute><RequireAuth><TaskList /></RequireAuth></InternalAppRoute>} />
          <Route path="/tasks/:taskId" element={<InternalAppRoute><RequireAuth><TaskList /></RequireAuth></InternalAppRoute>} />
          <Route path="/users" element={<InternalAppRoute><RequireAuth><UserList /></RequireAuth></InternalAppRoute>} />
          <Route path="/templates" element={<InternalAppRoute><RequireAuth><TemplateList /></RequireAuth></InternalAppRoute>} />
          <Route path="/ai" element={<InternalAppRoute><RequireAuth><RequireCompanyAiDeployment><RequireRole requiredRoles={['pm', 'finance', 'admin']} title="AI workspace access is limited" message="The company AI workspace is available to PM, finance, or admin users only."><AiWorkspacePage /></RequireRole></RequireCompanyAiDeployment></RequireAuth></InternalAppRoute>} />
          <Route path="/ai/admin" element={<InternalAppRoute><RequireAuth><RequireCompanyAiDeployment><RequireRole requiredRoles={['admin']} title="AI admin access is limited" message="The AI admin area is available to admin users only."><AiAdminPage /></RequireRole></RequireCompanyAiDeployment></RequireAuth></InternalAppRoute>} />
          <Route path="/ai/email" element={<InternalAppRoute><RequireAuth><RequireCompanyAiDeployment><RequireAiEmailReviewAccess><AiEmailPage /></RequireAiEmailReviewAccess></RequireCompanyAiDeployment></RequireAuth></InternalAppRoute>} />
          <Route path="/pdf-test" element={<InternalAppRoute><RequireAuth><PDFTest /></RequireAuth></InternalAppRoute>} />
          <Route path="/test-azure-sql" element={<InternalAppRoute><RequireAuth><TestAzureSQL /></RequireAuth></InternalAppRoute>} />
          <Route path="/styleguide" element={<InternalAppRoute><RequireAuth><Styleguide /></RequireAuth></InternalAppRoute>} />
          <Route path="/style-gallery" element={<InternalAppRoute><RequireAuth><RequireRole requiredRoles={['admin']} title="Style gallery access is limited" message="The internal style selection system is available to admin users only."><StyleGalleryPage /></RequireRole></RequireAuth></InternalAppRoute>} />

          {/* CATCH-ALL: Lowercase redirect for mixed-case URLs or 404 */}
          <Route path="*" element={<LowercaseRedirect />} />
        </Routes>
      </MobileLayout>
    </Router>
  );
};

export default AppRouter;
